'use client'

import { useState } from 'react'

import { usePathname } from 'next/navigation'

import { WhatsApp as WhatsAppIcon } from '@mui/icons-material'
import { Box, Button, ClickAwayListener, Fab, Grow, Tooltip, Typography, useMediaQuery } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { styled } from '@mui/material/styles'
import useScrollTrigger from '@mui/material/useScrollTrigger'

const ScrollToTopStyled = styled('div')<{ trigger?: boolean }>(({ theme, trigger }) => ({
  zIndex: theme.zIndex.tooltip,
  position: 'fixed',
  insetInlineEnd: theme.spacing(10),
  insetBlockEnd: trigger ? theme.spacing(28) : theme.spacing(14),
  transition: 'all 0.3s ease',
  transform: `translateY(${trigger ? '0' : '20px'})`
}))

const ModalBoxStyled = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '70px', // Distance above FAB
  right: '0',
  transform: 'translateX(-50%)',
  background: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  maxWidth: '90vw', // Prevent cutting off on smaller screens
  width: '300px',
  textAlign: 'center',
  zIndex: theme.zIndex.modal
}))

const WhatsAppFAB = () => {
  const [open, setOpen] = useState(false)

  const trigger = useScrollTrigger({
    threshold: 400,
    disableHysteresis: true
  })

  const handleToggle = () => setOpen(prev => !prev)
  const handleClose = () => setOpen(false)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const pathname = usePathname()

  if (pathname !== '/') return null

  return (
    <ScrollToTopStyled role='presentation' trigger={trigger}>
      <Tooltip title='Hubungi Kami' arrow>
        <Fab
          size='small'
          color='success'
          aria-label='whatsapp'
          onClick={handleToggle}
          sx={{
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              backgroundColor: '#128c7e',
              transform: 'scale(1.5)'
            }
          }}
        >
          <WhatsAppIcon />
        </Fab>
      </Tooltip>

      {/* Floating Modal */}
      {open && (
        <ClickAwayListener onClickAway={handleClose}>
          <Grow in={open} style={{ transformOrigin: 'center bottom' }}>
            <ModalBoxStyled
              sx={{
                maxWidth: isMobile ? '38vh' : '50vh',
                width: isMobile ? '38vh' : '50vh',
                borderRadius: '10px',
                padding: '16px'
              }}
            >
              <Typography variant='h6' component='h2' sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                Percakapan dengan TMC Eternal Home
              </Typography>

              <Box
                sx={{
                  backgroundColor: '#e1f5fe',
                  padding: '12px 16px',
                  borderRadius: '16px',
                  marginBottom: 2,
                  position: 'relative',
                  maxWidth: '80%',
                  marginLeft: 'auto', // Align the bubble to the right (sender's side)
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: '#0277bd',
                  fontWeight: 'normal',
                  mt: 7
                }}
              >
                <Typography variant='body2' sx={{ textAlign: 'left' }}>
                  Terima kasih telah menghubungi kami. Kami adalah TMC Eternal Home, siap membantu Anda dengan layanan
                  rumah duka dan kebutuhan kedukaan. Klik tombol di bawah untuk memulai percakapan dan mendapatkan
                  informasi lebih lanjut.
                </Typography>

                {/* Adding a small arrow to the bubble */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '-8px',
                    right: '12px',
                    width: 0,
                    height: 0,
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid #e1f5fe' // Arrow matches bubble color
                  }}
                />
              </Box>

              {/* WhatsApp Button with improved style */}
              <Button
                variant='contained'
                color='success'
                href='https://wa.me/6282118546605?text=Terima%20kasih%20telah%20menghubungi%20kami.%20Kami%20adalah%20TMC%20Eternal%20Home,%20siap%20membantu%20Anda%20dengan%20layanan%20rumah%20duka%20dan%20kebutuhan%20kedukaan.%20Klik%20tombol%20di%20bawah%20untuk%20memulai%20percakapan%20dan%20mendapatkan%20informasi%20lebih%20lanjut.'
                target='_blank'
                fullWidth
                sx={{
                  mt: 7,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  padding: '12px 0',
                  backgroundColor: '#25d366', // WhatsApp green color
                  '&:hover': {
                    backgroundColor: '#128c7e' // Darker green on hover
                  },
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                  fontSize: '14px'
                }}
              >
                Mulai Percakapan
              </Button>
            </ModalBoxStyled>
          </Grow>
        </ClickAwayListener>
      )}
    </ScrollToTopStyled>
  )
}

export default WhatsAppFAB
